import * as React from "react";
import type { User } from "./presence-schema";
import { usePresenceWithCursors } from "./use-cursors";
import { isChatActive } from "./Chat";

// Inject animation styles safely without requiring global CSS changes
if (typeof document !== 'undefined') {
  if (!document.getElementById('cursor-party-styles')) {
    const style = document.createElement('style');
    style.id = 'cursor-party-styles';
    style.innerHTML = `
      @keyframes cursor-walk {
        100% { background-position: -80px var(--cursor-y); }
      }
      .cursor-walking {
        animation: cursor-walk 0.16s steps(2) infinite;
      }
      .cursor-walking-slow {
        animation: cursor-walk 1s steps(2) infinite;
      }
    `;
    document.head.appendChild(style);
  }
}

const MASTER_SPRITE_URL = "https://cursor-party.h80h.xyz/cursor/sheet.png";

// Define cursor configurations using exact Y-offsets from the sprite sheet
const CURSOR_CONFIGS = [
  { spriteY: 40, chatColor: "#ddd53c" },  // 951
  { spriteY: 160, chatColor: "#8abade" }, // 8292
  { spriteY: 200, chatColor: "#88bbaa" }, // 9589
  { spriteY: 360, chatColor: "#9badb7" }, // 12162
  { spriteY: 0, chatColor: "#df7126" },   // 647
  { spriteY: 80, chatColor: "#bf9ff2" },  // 1669
  { spriteY: 120, chatColor: "#4ba1e4" }, // 3925
  { spriteY: 240, chatColor: "#ffb100" }, // 10334
  { spriteY: 280, chatColor: "#d6869e" }, // 11758
  { spriteY: 320, chatColor: "#5b6ee1" }, // 12011
];

// Cursors 647 (spriteY 0) and 10334 (spriteY 240) animate at 500ms/frame
// instead of the 80ms/frame used by every other cursor.
const SLOW_FRAME_SPRITE_Y = new Set([0, 240]);

// Verification icon component
function VerificationIcon() {
  return (
    <img
      src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNCIgaGVpZ2h0PSIxNCIgdmlld0JveD0iMCAwIDIwIDIwIj4KCTxwYXRoIGZpbGw9IiMzMzMiIGZpbGwtcnVsZT0iZXZlbm9kZCIgZD0iTTYuMjY3IDMuNDU1YTMuMDcgMy4wNyAwIDAgMCAxLjc0NS0uNzIzYTMuMDY2IDMuMDY2IDAgMCAxIDMuOTc2IDBhMy4wNyAzLjA3IDAgMCAwIDEuNzQ1LjcyM2EzLjA2NiAzLjA2NiAwIDAgMSAyLjgxMiAyLjgxMmMuMDUxLjY0My4zMDQgMS4yNTQuNzIzIDEuNzQ1YTMuMDY2IDMuMDY2IDAgMCAxIDAgMy45NzZhMy4wNyAzLjA3IDAgMCAwLS43MjMgMS43NDVhMy4wNjYgMy4wNjYgMCAwIDEtMi44MTIgMi44MTJhMy4wNyAzLjA3IDAgMCAwLTEuNzQ1LjcyM2EzLjA2NiAzLjA2NiAwIDAgMS0zLjk3NiAwYTMuMDcgMy4wNyAwIDAgMC0xLjc0NS0uNzIzYTMuMDY2IDMuMDY2IDAgMCAxLTIuODEyLTIuODEyYTMuMDcgMy4wNyAwIDAgMC0uNzIzLTEuNzQ1YTMuMDY2IDMuMDY2IDAgMCAxIDAtMy45NzZhMy4wNyAzLjA3IDAgMCAwIC43MjMtMS43NDVhMy4wNjYgMy4wNjYgMCAwIDEgMi44MTItMi44MTJtNy40NCA1LjI1MmExIDEgMCAwIDAtMS40MTQtMS40MTRMOSAxMC41ODZMNy43MDcgOS4yOTNhMSAxIDAgMCAwLTEuNDE0IDEuNDE0bDIgMmExIDEgMCAwIDAgMS40MTQgMHoiIGNsaXAtcnVsZT0iZXZlbm9kZCIgLz4KPC9zdmc+"
      alt="Verified"
      style={{
        width: "14px",
        height: "14px",
        marginLeft: "2px",
        filter: "brightness(0) invert(1)", // Makes the icon white
        display: "inline-block",
        verticalAlign: "middle"
      }}
    />
  );
}

// Animated typing indicator component
function AnimatedTypingIndicator() {
  const [dotCount, setDotCount] = React.useState(1);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setDotCount(prev => prev === 3 ? 1 : prev + 1);
    }, 500);

    return () => clearInterval(interval);
  }, []);

  return <span>{".".repeat(dotCount)}</span>;
}

export default function Cursor(props: {
  userId: string;
  fill: string;
  showChat: boolean;
  isOwnCursor?: boolean; // Add this prop to identify own cursor
  verifiedDomain?: string; // Add this prop to pass verified domain
}) {
  const [previousX, setPreviousX] = React.useState<number | null>(null);
  const [previousY, setPreviousY] = React.useState<number | null>(null);
  const [isFlipped, setIsFlipped] = React.useState(false);
  const [isMoving, setIsMoving] = React.useState(false);
  const movementTimeoutRef = React.useRef<number | null>(null);

  // Get user data - either from otherUsers or myself depending on isOwnCursor
  const user: User | null = usePresenceWithCursors(
    (state) => props.isOwnCursor 
      ? state.myself 
      : state.otherUsers.get(props.userId) || null
  );

  // Check if any other user is typing (has a message)
  const isAnyOtherUserTyping = usePresenceWithCursors((state) => {
    const otherUsers = Array.from(state.otherUsers.values());
    return otherUsers.some(user => user.presence?.message && user.presence.message.length > 0);
  });

  // Get cursorIndex from the user's presence data
  const cursorIndex = user?.presence?.cursorIndex ?? 0;
  if (!user?.presence?.cursor) return;
  const cursorConfig = CURSOR_CONFIGS[cursorIndex % CURSOR_CONFIGS.length];

  const cursor = {
    x: user.presence.cursor.x,
    y: user.presence.cursor.y,
    pointer: user.presence.cursor.pointer,
    message: user.presence.message ?? null,
  };

  // Get verification data - prioritize presence data over prop
  const verifiedDomain = user.presence.verifiedDomain || props.verifiedDomain || null;
  const isVerified = !!verifiedDomain;

  // Track movement direction and detect if cursor is moving
  React.useEffect(() => {
    if (previousX !== null && previousY !== null) {
      const deltaX = cursor.x - previousX;
      const distance = Math.sqrt(deltaX * deltaX + (cursor.y - previousY) * (cursor.y - previousY));
      
      // More sensitive flipping - flip on any horizontal movement
      if (Math.abs(deltaX) > 0.1) {
        setIsFlipped(deltaX < 0); // Flip when moving left (deltaX negative)
      }
      
      // Detect ANY movement - even smallest changes trigger walking
      if (distance > 0) {
        setIsMoving(true);
        
        // Clear existing timeout
        if (movementTimeoutRef.current) {
          clearTimeout(movementTimeoutRef.current);
        }
        
        // Set timeout to mark cursor as stationary after no movement
        movementTimeoutRef.current = setTimeout(() => {
          setIsMoving(false);
        }, 100); // 100ms delay before considering cursor stationary
      }
    } else {
      // On first render, assume cursor might be moving
      setIsMoving(true);
      movementTimeoutRef.current = setTimeout(() => {
        setIsMoving(false);
      }, 100);
    }
    setPreviousX(cursor.x);
    setPreviousY(cursor.y);
  }, [cursor.x, cursor.y, previousX, previousY]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (movementTimeoutRef.current) {
        clearTimeout(movementTimeoutRef.current);
      }
    };
  }, []);

  const offset = 10;

  // Modified visibility logic:
  // - Show own cursor when chat is active OR when any other user is typing
  // - Show other cursors with different opacity based on chat state
  const getStyles = () => {
    if (props.isOwnCursor) {
      // Show own cursor when YOU are typing OR when any other user is typing
      if (!isChatActive && !isAnyOtherUserTyping) {
        return { display: 'none' };
      }
      // Show own cursor prominently when chat is active or others are typing
        return {
          filter: "blur(0px)",
          opacity: 1,
          zIndex: 1002, // Higher than other cursors
          transform: `translate(${cursor.x - offset}px, ${cursor.y - offset}px) translateZ(0)`,
          WebkitTransform: `translate(${cursor.x - offset}px, ${cursor.y - offset}px) translateZ(0)`,
        };
    } else {
      // Other users' cursors - show verified domain when chat is active
      return !props.showChat || cursor.message !== null || (isChatActive && isVerified)
        ? {
            opacity: 1,
            zIndex: 1001,
            transform: `translate(${cursor.x - offset}px, ${cursor.y - offset}px) translateZ(0)`,
            WebkitTransform: `translate(${cursor.x - offset}px, ${cursor.y - offset}px) translateZ(0)`,
          }
        : {
            filter: "blur(0.4px)",
            opacity: 0.4,
            zIndex: -1,
            transform: `translate(${cursor.x - offset}px, ${cursor.y - offset}px) translateZ(0)`,
            WebkitTransform: `translate(${cursor.x - offset}px, ${cursor.y - offset}px) translateZ(0)`,
          };
    }
  };

  const styles = getStyles();

  // Helper function to get base bubble styles (shared between chat and username)
  const getBaseBubbleStyles = () => ({
    position: "absolute" as const,
    fontStyle: "normal" as const,
    fontFamily:
      'system-ui, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
    color: "white",
    borderRadius: "24px",
    whiteSpace: "nowrap" as const,
    textOverflow: "ellipsis" as const,
    minWidth: "0px",
    backgroundColor: cursorConfig.chatColor,
    textAlign: "end" as const
  });

  // Helper function to get chat bubble position styles
  const getChatBubbleStyles = () => {
    const baseStyles = {
      ...getBaseBubbleStyles(),
      fontSize: "16px",
      fontWeight: "400" as const,
      padding: "6px 15px",
      top: "-18px"
    };

    // Position the chat bubble on the opposite side when flipped
    if (isFlipped) {
      return {
        ...baseStyles,
        left: "35px", // Position on the right side
        right: "auto"
      };
    } else {
      return {
        ...baseStyles,
        right: "35px" // Default position on the left side
      };
    }
  };

  // Helper function to get username bubble position styles
  const getUsernameBubbleStyles = () => {
    const baseStyles = {
      ...getBaseBubbleStyles(),
      fontSize: "12px",
      fontWeight: "500" as const,
      padding: "3px 3px 3px 6px",
      top: "-46px", // Position above the chat bubble
      display: "flex",
      alignItems: "center",
      minWidth: "fit-content", // Ensure bubble expands to fit content
      width: "auto"
    };

    // Position the username bubble on the opposite side when flipped
    if (isFlipped) {
      return {
        ...baseStyles,
        left: "35px", // Position on the right side
        right: "auto"
      };
    } else {
      return {
        ...baseStyles,
        right: "35px" // Default position on the left side
      };
    }
  };

  // Fixed condition for showing the verified domain bubble
  const showVerifiedDomain = React.useMemo(() => {
    if (!isVerified || !verifiedDomain) {
      return false;
    }
    
    // Show for verified users when:
    // 1. They have a message (typing or sent)
    // 2. OR they are the owner and chat is active (regardless of message state)
    return cursor.message !== null || (props.isOwnCursor && isChatActive);
  }, [isVerified, verifiedDomain, cursor.message, props.isOwnCursor, isChatActive]);

  return (
    <div
      style={{
        position: "absolute",
        pointerEvents: "none", // Critical for touch devices
        touchAction: "none",   // Prevent touch interference
        userSelect: "none",    // Prevent text selection
        willChange: "transform", // Hint for hardware acceleration
        backfaceVisibility: "hidden", // Prevent flicker
        WebkitBackfaceVisibility: "hidden", // Safari-specific
        ...styles,
      }}
    >
      {cursor.pointer === "mouse" ? (
        <MousePointer 
          fill={props.fill} 
          isFlipped={isFlipped} 
          isMoving={isMoving} 
          cursorConfig={cursorConfig}
        />
      ) : (
        <TouchPointer 
          fill={props.fill} 
          isFlipped={isFlipped} 
          isMoving={isMoving} 
          cursorConfig={cursorConfig}
        />
      )}
      
      {/* Username bubble for verified users */}
      {showVerifiedDomain ? (
        <div style={getUsernameBubbleStyles()}>
          <span>{verifiedDomain}</span>
          <VerificationIcon />
        </div>
      ) : null}
      
      {/* Chat-related bubbles */}
      {isChatActive && cursor.message === null && props.isOwnCursor && (
        <div style={getChatBubbleStyles()}>
          <AnimatedTypingIndicator />
        </div>
      )}
      {!isChatActive && isAnyOtherUserTyping && props.isOwnCursor && (
        <div style={getChatBubbleStyles()}>
          Type / to reply
        </div>
      )}
      {cursor.message !== null && (
        <div style={getChatBubbleStyles()}>
          {cursor.message}
        </div>
      )}
    </div>
  );
}

function MousePointer(props: { 
  fill: string; 
  isFlipped: boolean; 
  isMoving: boolean;
  cursorConfig: typeof CURSOR_CONFIGS[0]; // Corrected signature
}) {
  const isSlowFrame = SLOW_FRAME_SPRITE_Y.has(props.cursorConfig.spriteY);
  return (
    <div
      className={props.isMoving ? (isSlowFrame ? "cursor-walking-slow" : "cursor-walking") : ""}
      style={{
        width: "40px",
        height: "40px",
        backgroundImage: `url(${MASTER_SPRITE_URL})`,
        backgroundSize: "80px 400px",
        backgroundRepeat: "no-repeat",
        "--cursor-y": `-${props.cursorConfig.spriteY}px`,
        backgroundPosition: `0px var(--cursor-y)`,
        filter: "drop-shadow(1px 1px 1px rgba(0,0,0,0.3))",
        transform: `translateZ(0) ${props.isFlipped ? 'scaleX(-1)' : ''}`, // Prevents blurring on some browsers
        WebkitTransform: `translateZ(0) ${props.isFlipped ? 'scaleX(-1)' : ''}`,
        pointerEvents: "none", // Prevent cursor from interfering with clicks
        WebkitMaskImage: '-webkit-radial-gradient(white, black)',
      } as React.CSSProperties}
    />
  );
}

function TouchPointer(props: { 
  fill: string; 
  isFlipped: boolean; 
  isMoving: boolean;
  cursorConfig: typeof CURSOR_CONFIGS[0]; // Corrected signature
}) {
  const isSlowFrame = SLOW_FRAME_SPRITE_Y.has(props.cursorConfig.spriteY);
  return (
    <div
      className={props.isMoving ? (isSlowFrame ? "cursor-walking-slow" : "cursor-walking") : ""}
      style={{
        width: "40px",
        height: "40px",
        backgroundImage: `url(${MASTER_SPRITE_URL})`,
        backgroundSize: "80px 400px",
        backgroundRepeat: "no-repeat",
        "--cursor-y": `-${props.cursorConfig.spriteY}px`,
        backgroundPosition: `0px var(--cursor-y)`,
        transform: `translateZ(0) ${props.isFlipped ? 'scaleX(-1)' : ''}`,
        WebkitTransform: `translateZ(0) ${props.isFlipped ? 'scaleX(-1)' : ''}`,
        WebkitMaskImage: '-webkit-radial-gradient(white, black)',
        pointerEvents: "none",
      } as React.CSSProperties}
    />
  );
}